import React from 'react'
import cls from 'classnames'
import styles from './button.module.scss';
import { Busy } from '../busy';

export type ButtonWeight = 'primary' | 'normal' | 'simple' | 'simple-dark';

export interface ButtonProps extends React.InputHTMLAttributes<HTMLButtonElement | HTMLLabelElement> {
  weight?: ButtonWeight;
  iconType?: 'regular' | 'solid';
  smallCaps?: boolean;
  testId?: string;

  /**
   * Whether to show a small badge.
   */
  showBadge?: boolean;

  /**
   * Whether to offset badge in case of narrow buttons.
   */
  offsetBadge?: boolean;

  /**
   * Whether to grow the body of this button. This will align the icon at the side of the button.
   */
  growBody?: boolean;

  busy?: boolean;

  download?: string;

  /**
   * Href to open in a new tab.
   */
  externalLinkTo?: string;

  /**
   * Extra background style parameter.
   */
  background?: 'white' | 'translucent-white';
}

/**
 * Basic button component.
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  offsetBadge,
  showBadge,
  growBody,
  weight = 'normal',
  busy,
  ...props
}) => {
  return (
    <ButtonRoot
      {...props}
      busy={busy}
      weight={weight}>
      {
        children
      }
      {
        busy ? (
          <Busy className={styles.busy} />
        ) : null
      }
    </ButtonRoot>
  )
}

const ButtonRoot: React.FC<ButtonProps> = ({
  testId,
  children,
  externalLinkTo,
  ...props
}) => {
  const rootProps: any = {
    ...props,
    "data-testid": testId,
    "data-weight": props.weight,
    className: cls([
      props.className,
      styles.button,
      props.busy ? styles.isBusy : null,
      props.background ? `background-${props.background}` : null
    ])
  }

  if (typeof externalLinkTo === "string") {
    const isHash = /^#\w+/.test(externalLinkTo);

    return (
      <a {...rootProps} target={isHash ? "_self" : "_blank"}
        rel="noopener noreferrer" href={externalLinkTo}>
        {children}
      </a>
    );
  }

  return (
    <button {...rootProps}>{children}</button>
  );
}